import React from 'react';
import css from './ContactCluster.module.scss';

export interface ContactFace {
  initials: string;
  name: string;
}

interface ContactClusterProps {
  faces: ContactFace[];
  total: number;
  maxFaces?: number;
}

// A few brand-adjacent tints so the cluster reads as distinct people without
// leaving the palette. Index-based, so it's stable across renders.
const TINTS = [
  { bg: '#dfeae5', fg: '#185d40' },
  { bg: '#fdf1d6', fg: '#b45309' },
  { bg: '#e6ecf5', fg: '#3b5573' },
  { bg: '#eae6f2', fg: '#5b4a86' },
  { bg: '#e3efe9', fg: '#207551' },
];

/**
 * Real faces behind the "Reached out" number — initials monograms from the most
 * recent contacted applications, with a +N overflow keyed on the true total
 * (which is larger than the handful we fetch for avatars).
 */
const ContactCluster: React.FC<ContactClusterProps> = ({ faces, total, maxFaces = 5 }) => {
  // Never render more faces than the headline count (totalContacted can be lower
  // than the number of applications we fetched faces from), and never a negative
  // overflow badge.
  const shown = faces.slice(0, Math.min(maxFaces, Math.max(0, total)));
  const overflow = Math.max(0, total - shown.length);

  return (
    <div className={css.cluster} aria-label={`${total} people reached`}>
      {shown.map((face, i) => {
        const tint = TINTS[i % TINTS.length];
        return (
          <span
            key={`${face.initials}-${i}`}
            className={css.avatar}
            style={{ backgroundColor: tint.bg, color: tint.fg }}
            title={face.name}
            aria-hidden="true"
          >
            {face.initials}
          </span>
        );
      })}
      {overflow > 0 && (
        <span className={`${css.avatar} ${css.overflow}`} aria-hidden="true">
          +{overflow}
        </span>
      )}
    </div>
  );
};

export default ContactCluster;
